import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageSquare, 
  Send, 
  Loader2, 
  ShoppingCart, 
  Sparkles, 
  MapPin,
  Plus,
  Check,
  User,
  Bot,
  Lightbulb,
  RefreshCw,
  History,
  Copy,
  X,
  Maximize2,
  Minimize2,
  Store
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import api from '../../utils/api';
import { toast } from 'sonner';
import ExploreDrlpsModal from './ExploreDrlpsModal';
import { useLanguage } from '../../contexts/LanguageContext';
import { ISO_TO_LEGACY, UI_TRANSLATIONS } from './dsa/translations';


// Detect language from message content OR user query
const detectLanguageFromContent = (content) => {
  if (!content) return 'english';
  const text = content.toLowerCase();
  
  // Check for Spanish indicators
  if (text.includes('¿es esto correcto?') || text.includes('interpretación') || text.includes('porciones:') ||
      text.includes('por favor') || text.includes('comida') || text.includes('platos') || 
      text.includes('personas') || text.includes('niños') || text.includes('receta')) return 'spanish';
  
  // Check for French indicators
  if (text.includes('est-ce correct?') || text.includes('interprétation') || text.includes('portions:') ||
      text.includes('s\'il vous plaît') || text.includes('repas') || text.includes('personnes')) return 'french';
  
  // Check for German indicators
  if (text.includes('ist das richtig?') || text.includes('interpretation') || text.includes('portionen:') ||
      text.includes('bitte') || text.includes('mahlzeit') || text.includes('personen')) return 'german';
  
  // Check for Italian indicators
  if (text.includes('è corretto?') || text.includes('interpretazione') || text.includes('porzioni:') ||
      text.includes('per favore') || text.includes('pasto') || text.includes('persone')) return 'italian';
  
  // Check for Portuguese indicators
  if (text.includes('está correto?') || text.includes('interpretação') || text.includes('porções:') ||
      text.includes('por favor') || text.includes('refeição') || text.includes('pessoas')) return 'portuguese';
  
  return 'english';
};

// =============================================================================
// RECIPE CONTENT TRANSLATION HELPER
// =============================================================================
// Translates English recipe patterns in the agent's response to the user's language
const translateRecipeContent = (content, language) => {
  if (!content || language === 'english') return content;
  
  const t = UI_TRANSLATIONS[language] || UI_TRANSLATIONS.english;
  let translated = content;
  
  // Course type mappings for translation
  const courseTypeMap = {
    'Appetizer': t.appetizer,
    'Soup': t.soup,
    'Salad': t.salad,
    'Fish': t.fish,
    'Pasta': t.pasta,
    'Rice/Risotto': t.riceRisotto,
    'Rice': t.riceRisotto,
    'Risotto': t.riceRisotto,
    'Vegetable': t.vegetable,
    'Palate Cleanser': t.palateCleanser,
    'Main': t.main,
    'Cheese': t.cheese,
    'Dessert': t.dessert,
    'Drinks/Digestif': t.drinkDigestif,
    'Drinks': t.drinkDigestif,
    'Digestif': t.drinkDigestif
  };
  
  // Translate "Course X - Type: Name" patterns
  // Match patterns like "Course 1 - Appetizer: Mezze Platter" or "### Course 4 - Fish: Tandoori Fish"
  translated = translated.replace(
    /([#*]*\s*)?Course\s+(\d+)\s*[-–]\s*([^:]+):\s*/gi,
    (match, prefix, num, type) => {
      const cleanType = type.trim();
      const translatedType = courseTypeMap[cleanType] || cleanType;
      const prefixStr = prefix || '';
      return `${prefixStr}${t.course} ${num} - ${translatedType}: `;
    }
  );
  
  // Translate "Available Inventory" section header
  translated = translated.replace(
    /([#*]*\s*)?Available Inventory(\s*Section)?/gi,
    (match, prefix) => `${prefix || ''}${t.availableInventory}`
  );
  
  // Translate "Ingredients We Couldn't Source:" header
  translated = translated.replace(
    /([#*]*\s*)?Ingredients We Couldn't Source:?/gi,
    (match, prefix) => `${prefix || ''}${t.ingredientsCouldntSource}`
  );
  
  // Translate "SOLD OUT" labels
  translated = translated.replace(/SOLD OUT/gi, t.soldOut);
  
  // Translate "available" text (e.g., "5 available")
  translated = translated.replace(/(\d+)\s+available/gi, `$1 ${t.available}`);
  
  // Translate "Add to Cart" buttons/text
  translated = translated.replace(/Add to Cart/gi, t.addToCart);
  
  return translated;
};

/**
 * DealShaqAgent - Conversational AI Food Companion
 * 
 * A true conversational agent that:
 * - Maintains multi-turn conversation memory
 * - Has deep food domain knowledge  
 * - Proactively shares insights
 * - Has a warm, helpful personality
 * - Remembers user preferences
 */
const DealShaqAgent = ({ onAddToCart, dacProfile, userName }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  // Track added items PER MESSAGE INDEX to prevent cross-contamination
  // Format: { messageIndex: Set<rshd_id> }
  const [addedItemsByMessage, setAddedItemsByMessage] = useState({});
  const [cartItems, setCartItems] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  // Last exchange from previous session
  const [lastExchange, setLastExchange] = useState(null);
  const [showLastExchange, setShowLastExchange] = useState(false);
  // Full screen mode
  const [isFullScreen, setIsFullScreen] = useState(false);
  // Expanded search indicator
  const [isSearchingExpanded, setIsSearchingExpanded] = useState(false);
  const [expandedSearchInfo, setExpandedSearchInfo] = useState(null);
  // Explore DRLPs modal
  const [showExploreDrlps, setShowExploreDrlps] = useState(false);
  // UI language: driven by the global LanguageContext (header selector).
  // This is the single source of truth so the header dropdown always wins.
  const { locale } = useLanguage();
  const uiLanguage = ISO_TO_LEGACY[locale] || 'english';
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Get translations for current language
  const t = UI_TRANSLATIONS[uiLanguage] || UI_TRANSLATIONS.english;
  
  // Helper to check if item is added for a specific message
  const isItemAddedForMessage = (messageIndex, rshd_id) => {
    return addedItemsByMessage[messageIndex]?.has(rshd_id) || false;
  };
  
  // Helper to mark items as added for a specific message
  const markItemsAddedForMessage = (messageIndex, rshd_ids) => {
    setAddedItemsByMessage(prev => ({
      ...prev,
      [messageIndex]: new Set([...(prev[messageIndex] || []), ...rshd_ids])
    }));
  };
  
  // Extract first name from userName prop or dacProfile
  const firstName = userName?.split(' ')[0] || dacProfile?.name?.split(' ')[0] || '';

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Fetch cart on mount
  useEffect(() => {
    fetchCart();
  }, []);
  
  // Fetch last exchange from previous session on mount
  useEffect(() => {
    fetchLastExchange();
  }, []);
  
  // Fetch last exchange from previous session
  const fetchLastExchange = async () => {
    try {
      const response = await api.get('/nlu/agent/last-exchange');
      if (response.data.has_previous_exchange) {
        setLastExchange(response.data);
        setShowLastExchange(true);
      }
    } catch (err) {
      console.error('Error fetching last exchange:', err);
    }
  };
  
  // Continue from last exchange
  const continueFromLastExchange = () => {
    if (!lastExchange) return;
    
    // Restore the conversation from last exchange
    const restoredMessages = [];
    
    // Add the user's last query as a message
    restoredMessages.push({
      role: 'user',
      content: lastExchange.user_query
    });
    
    // Add the agent's response with cart items if available
    if (lastExchange.agent_response) {
      restoredMessages.push({
        role: 'assistant',
        content: lastExchange.agent_response,
        cart_items: lastExchange.cart_items || []
      });
    }
    
    // Set the messages to restore the conversation
    setMessages(restoredMessages);
    
    // Restore the session ID if available
    if (lastExchange.session_id) {
      setSessionId(lastExchange.session_id);
    }
    
    // Hide the prompt
    setShowLastExchange(false);
    
    toast.success('Previous conversation restored!', { icon: '✨' });
  };
  
  // Dismiss last exchange prompt
  const dismissLastExchange = () => {
    setShowLastExchange(false);
  };
  
  // Fetch cart from backend
  const fetchCart = async () => {
    try {
      setCartLoading(true);
      const response = await api.get('/cart');
      setCartItems(response.data.items || []);
    } catch (err) {
      console.error('Error fetching cart:', err);
    } finally {
      setCartLoading(false);
    }
  };
  
  // Calculate number of actual requests (not confirmations)
  const getRequestCount = () => {
    // Count user messages that are NOT short confirmations
    return messages.filter(m => 
      m.role === 'user' && 
      !m.isWelcome && 
      m.content.length > 30  // Confirmations like "Yes, proceed" are short
    ).length;
  };
  
  // Copy conversation to clipboard
  const copyConversation = () => {
    const conversationText = messages
      .filter(m => !m.isWelcome)
      .map(m => {
        const role = m.role === 'user' ? 'You' : 'DealShaq Shopping Assistant (DSA)';
        return `${role}:\n${m.content}\n`;
      })
      .join('\n---\n\n');
    
    navigator.clipboard.writeText(conversationText).then(() => {
      toast.success('Conversation copied to clipboard', { icon: '📋' });
    }).catch(() => {
      toast.error('Failed to copy conversation');
    });
  };
  // Welcome message on first load - now multilingual
  // Also updates when language changes
  useEffect(() => {
    const greetings = {
      english: firstName ? `Hey ${firstName}! 👋` : `Hey there! 👋`,
      spanish: firstName ? `¡Hola ${firstName}! 👋` : `¡Hola! 👋`,
      french: firstName ? `Salut ${firstName}! 👋` : `Salut! 👋`,
      german: firstName ? `Hallo ${firstName}! 👋` : `Hallo! 👋`,
      italian: firstName ? `Ciao ${firstName}! 👋` : `Ciao! 👋`,
      portuguese: firstName ? `Olá ${firstName}! 👋` : `Olá! 👋`,
      korean: firstName ? `안녕하세요 ${firstName}님! 👋` : `안녕하세요! 👋`,
      chinese: firstName ? `你好 ${firstName}! 👋` : `你好! 👋`,
      arabic: firstName ? `مرحبًا ${firstName}! 👋` : `مرحبًا! 👋`
    };
    
    const welcomeMessages = {
      english: `${greetings.english} I'm your DealShaq Shopping Assistant (DSA) - think of me as your personal food shopping companion who knows all the sizzling hot deals and a thing or two about cooking.

${t.iCanHelpYou}
• ${t.discoverDeals}
• ${t.shareTips}
• ${t.findShoppingList}
• ${t.findIngredients}
• Multiple grocers with sizzling hot deals on your recipe ingredients

${t.whatLookingFor}`,
      spanish: `${greetings.spanish} Soy tu Asistente de Compras DealShaq (DSA) - piensa en mí como tu compañero personal de compras de alimentos que conoce todas las ofertas candentes y sabe un poco de cocina.

${t.iCanHelpYou}
• ${t.discoverDeals}
• ${t.shareTips}
• ${t.findShoppingList}
• ${t.findIngredients}
• Varios supermercados con ofertas candentes en los ingredientes de tus recetas

${t.whatLookingFor}`,
      french: `${greetings.french} Je suis votre Assistant Shopping DealShaq (DSA) - considérez-moi comme votre compagnon personnel pour les courses alimentaires qui connaît toutes les offres brûlantes et s'y connaît en cuisine.

${t.iCanHelpYou}
• ${t.discoverDeals}
• ${t.shareTips}
• ${t.findShoppingList}
• ${t.findIngredients}
• Plusieurs épiceries avec des offres brûlantes sur les ingrédients de vos recettes

${t.whatLookingFor}`,
      german: `${greetings.german} Ich bin Ihr DealShaq-Einkaufsassistent (DSA) - betrachten Sie mich als Ihren persönlichen Einkaufsbegleiter für Lebensmittel, der alle heißen Angebote kennt und ein wenig vom Kochen versteht.

${t.iCanHelpYou}
• ${t.discoverDeals}
• ${t.shareTips}
• ${t.findShoppingList}
• ${t.findIngredients}
• Mehrere Lebensmittelhändler mit heißen Angeboten auf Ihre Rezeptzutaten

${t.whatLookingFor}`,
      italian: `${greetings.italian} Sono il tuo Assistente Shopping DealShaq (DSA) - pensami come il tuo compagno personale per la spesa alimentare che conosce tutte le offerte più calde e sa qualcosa di cucina.

${t.iCanHelpYou}
• ${t.discoverDeals}
• ${t.shareTips}
• ${t.findShoppingList}
• ${t.findIngredients}
• Diversi negozi di alimentari con offerte calde sugli ingredienti delle tue ricette

${t.whatLookingFor}`,
      portuguese: `${greetings.portuguese} Sou seu Assistente de Compras DealShaq (DSA) - pense em mim como seu companheiro pessoal de compras de alimentos que conhece todas as ofertas imperdíveis e sabe um pouco de culinária.

${t.iCanHelpYou}
• ${t.discoverDeals}
• ${t.shareTips}
• ${t.findShoppingList}
• ${t.findIngredients}
• Vários supermercados com ofertas imperdíveis nos ingredientes das suas receitas

${t.whatLookingFor}`,
      korean: `${greetings.korean} 저는 DealShaq 쇼핑 어시스턴트 (DSA)입니다 - 뜨거운 할인과 요리에 대해 잘 아는 개인 식품 쇼핑 동반자라고 생각해 주세요.

${t.iCanHelpYou}
• ${t.discoverDeals}
• ${t.shareTips}
• ${t.findShoppingList}
• ${t.findIngredients}
• 레시피 재료에 대한 뜨거운 할인을 제공하는 여러 식료품점

${t.whatLookingFor}`,
      chinese: `${greetings.chinese} 我是您的 DealShaq 购物助手 (DSA) - 把我想象成您的个人食品购物伙伴,了解所有火热优惠并对烹饪略知一二。

${t.iCanHelpYou}
• ${t.discoverDeals}
• ${t.shareTips}
• ${t.findShoppingList}
• ${t.findIngredients}
• 多家食品店针对您食谱食材的火热优惠

${t.whatLookingFor}`,
      arabic: `${greetings.arabic} أنا مساعد التسوق الخاص بك من DealShaq (DSA) - فكر بي كرفيقك الشخصي لتسوق الطعام الذي يعرف جميع العروض الساخنة ولديه بعض المعرفة في الطبخ.

${t.iCanHelpYou}
• ${t.discoverDeals}
• ${t.shareTips}
• ${t.findShoppingList}
• ${t.findIngredients}
• العديد من محلات البقالة بعروض ساخنة على مكونات وصفاتك

${t.whatLookingFor}`
    };
    
    // If no messages yet, create welcome message
    if (messages.length === 0) {
      setMessages([{
        role: 'agent',
        content: welcomeMessages[uiLanguage] || welcomeMessages.english,
        timestamp: new Date(),
        isWelcome: true
      }]);
    } 
    // If first message is welcome and language changed, update it
    else if (messages.length === 1 && messages[0].isWelcome) {
      const currentContent = messages[0].content;
      const newContent = welcomeMessages[uiLanguage] || welcomeMessages.english;
      // Only update if content actually changed
      if (currentContent !== newContent) {
        setMessages([{
          ...messages[0],
          content: newContent
        }]);
      }
    }
  }, [firstName, uiLanguage, t, messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Check if user is accepting an expanded search offer
    const isAcceptingExpandedSearch = expandedSearchInfo && 
      /\b(yes|sure|ok|okay|search|check|find|look)\b/i.test(userMessage.toLowerCase());
    
    if (isAcceptingExpandedSearch) {
      setIsSearchingExpanded(true);
    }
    
    // Add user message to chat
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    }]);

    setIsLoading(true);

    try {
      const response = await api.post('/nlu/agent/chat', {
        message: userMessage,
        session_id: sessionId
      });

      // Save session ID for conversation continuity
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
      }

      // Add agent response to chat
      setMessages(prev => [...prev, {
        role: 'agent',
        content: response.data.message,
        timestamp: new Date(),
        cartItems: response.data.cart_items,
        sourcingPlan: response.data.sourcing_plan,
        insights: response.data.insights_shared,
        conversationTurn: response.data.conversation_turn,
        awaitingConfirmation: response.data.awaiting_confirmation,
        interpretedRequest: response.data.interpreted_request,
        proactiveOffer: response.data.proactive_offer,
        noPreferredStores: response.data.no_preferred_stores,
        showExploreDrlps: response.data.show_explore_drlps
      }]);
      
      // If there's a proactive offer, store it for UI display
      if (response.data.proactive_offer) {
        setExpandedSearchInfo(response.data.proactive_offer);
      } else {
        setExpandedSearchInfo(null);
      }

    } catch (err) {
      console.error('Agent error:', err);
      setMessages(prev => [...prev, {
        role: 'agent',
        content: "I'm having a moment - could you try that again? If it keeps happening, try refreshing the page.",
        timestamp: new Date(),
        isError: true
      }]);
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
      setIsSearchingExpanded(false);
      inputRef.current?.focus();
    }
  };

  const handleAddToCart = async (item, messageIndex) => {
    try {
      // Call the cart API to persist the item
      const response = await api.post('/cart/add', {
        rshd_id: item.rshd_id,
        quantity: item.suggested_quantity || 1,
        item_name: item.item_name,
        store_id: item.store_id,
        store_name: item.store_name,
        price_per_unit: item.price_per_unit,
        matched_ingredient: item.matched_ingredient
      });
      
      if (response.data.success) {
        // Mark item as added for THIS specific message only
        markItemsAddedForMessage(messageIndex, [item.rshd_id]);
        toast.success(`Added ${item.item_name} to cart`, { icon: '🛒' });
        
        // Refresh cart
        fetchCart();
        
        // Also call the parent callback if provided
        if (onAddToCart) {
          onAddToCart({
            rshd_id: item.rshd_id,
            name: item.item_name,
            drlp_id: item.store_id,
            drlp_name: item.store_name,
            quantity: item.suggested_quantity,
            deal_price: item.price_per_unit
          });
        }
      } else {
        toast.error(response.data.message || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error(error.response?.data?.detail || 'Failed to add item to cart');
    }
  };
  
  // Add all cart items at once
  const handleAddAllToCart = async (cartItemsToAdd, messageIndex) => {
    if (!cartItemsToAdd || cartItemsToAdd.length === 0) return;
    
    try {
      const items = cartItemsToAdd.map(item => ({
        rshd_id: item.rshd_id,
        quantity: item.suggested_quantity || 1,
        item_name: item.item_name,
        store_id: item.store_id,
        store_name: item.store_name,
        price_per_unit: item.price_per_unit,
        matched_ingredient: item.matched_ingredient
      }));
      
      const response = await api.post('/cart/add-all', items);
      
      if (response.data.success) {
        const addedIds = response.data.added_items.map(i => i.rshd_id);
        // Mark items as added for THIS specific message only
        markItemsAddedForMessage(messageIndex, addedIds);
        toast.success(`Added ${response.data.added_items.length} items to cart`, { icon: '🛒' });
        
        // Refresh cart
        fetchCart();
      }
    } catch (error) {
      console.error('Error adding all to cart:', error);
      toast.error('Failed to add items to cart');
    }
  };
  
  // Remove item from cart
  const handleRemoveFromCart = async (rshd_id) => {
    try {
      await api.delete(`/cart/${rshd_id}`);
      toast.success('Item removed from cart');
      fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast.error('Failed to remove item');
    }
  };
  
  // Clear entire cart
  const handleClearCart = async () => {
    try {
      await api.delete('/cart');
      setCartItems([]);
      toast.success('Cart cleared');
    } catch (error) {
      console.error('Error clearing cart:', error);
      toast.error('Failed to clear cart');
    }
  };

  const startNewConversation = () => {
    setSessionId(null);
    setMessages([{
      role: 'agent',
      content: `Fresh start! 🌟 What can I help you find today?`,
      timestamp: new Date(),
      isWelcome: true
    }]);
    setAddedItemsByMessage({});
  };

  const renderMessage = (message, index) => {
    const isUser = message.role === 'user';
    
    return (
      <div 
        key={index}
        className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div className={`flex items-start gap-3 max-w-[85%] ${isUser ? 'flex-row-reverse' : ''}`}>
          {/* Avatar */}
          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
            isUser 
              ? 'bg-blue-500 text-white' 
              : 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white'
          }`}>
            {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
          </div>
          
          {/* Message Content */}
          <div className={`rounded-2xl px-4 py-3 ${
            isUser 
              ? 'bg-blue-500 text-white rounded-tr-sm' 
              : message.isError 
                ? 'bg-red-50 text-red-800 border border-red-200 rounded-tl-sm'
                : 'bg-white border border-gray-200 shadow-sm rounded-tl-sm'
          }`}>
            {/* Message text with markdown-like formatting */}
            <div className={`whitespace-pre-wrap ${isUser ? '' : 'text-gray-800'}`}>
              {translateRecipeContent(message.content, uiLanguage).split('\n').map((line, i) => {
                // Handle headers
                if (line.startsWith('### ')) {
                  return <h4 key={i} className="font-bold text-base mt-3 mb-2">{line.replace('### ', '')}</h4>;
                }
                if (line.startsWith('#### ')) {
                  return <h5 key={i} className="font-semibold text-sm mt-2 mb-1">{line.replace('#### ', '')}</h5>;
                }
                // Handle bullet points
                if (line.startsWith('- ') || line.startsWith('• ')) {
                  return <p key={i} className="ml-2 my-0.5">• {line.replace(/^[-•]\s*/, '')}</p>;
                }
                // Handle bold text
                if (line.includes('**')) {
                  const parts = line.split(/\*\*(.*?)\*\*/g);
                  return (
                    <p key={i} className="my-1">
                      {parts.map((part, j) => 
                        j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                      )}
                    </p>
                  );
                }
                return line ? <p key={i} className="my-1">{line}</p> : <br key={i} />;
              })}
            </div>

            {/* Confirmation Buttons - Show when agent is awaiting confirmation */}
            {message.awaitingConfirmation && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      // Detect language from message content and respond appropriately
                      const isSpanish = message.content.includes('¿Es esto correcto?') || message.content.includes('interpretación');
                      const isFrench = message.content.includes('Est-ce correct?') || message.content.includes('interprétation');
                      const isGerman = message.content.includes('Ist das richtig?') || message.content.includes('Interpretation');
                      const isItalian = message.content.includes('È corretto?') || message.content.includes('interpretazione');
                      const isPortuguese = message.content.includes('Está correto?') || message.content.includes('interpretação');
                      
                      let confirmText = 'Yes, that\'s correct. Please proceed.';
                      if (isSpanish) confirmText = 'Sí, es correcto. Por favor, continúa.';
                      else if (isFrench) confirmText = 'Oui, c\'est correct. Veuillez continuer.';
                      else if (isGerman) confirmText = 'Ja, das ist richtig. Bitte fortfahren.';
                      else if (isItalian) confirmText = 'Sì, è corretto. Per favore, procedi.';
                      else if (isPortuguese) confirmText = 'Sim, está correto. Por favor, continue.';
                      
                      setInputValue(confirmText);
                      setTimeout(() => document.querySelector('form')?.requestSubmit(), 100);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="confirm-interpretation-btn"
                  >
                    <Check className="w-4 h-4 mr-1" /> 
                    {message.content.includes('¿Es esto correcto?') ? 'Sí, continuar' :
                     message.content.includes('Est-ce correct?') ? 'Oui, continuer' :
                     message.content.includes('Ist das richtig?') ? 'Ja, weiter' :
                     message.content.includes('È corretto?') ? 'Sì, procedi' :
                     message.content.includes('Está correto?') ? 'Sim, continuar' :
                     'Yes, proceed'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setInputValue('');
                      inputRef.current?.focus();
                    }}
                    className="border-gray-300"
                    data-testid="modify-request-btn"
                  >
                    {message.content.includes('¿Es esto correcto?') ? 'No, aclarar' :
                     message.content.includes('Est-ce correct?') ? 'Non, clarifier' :
                     message.content.includes('Ist das richtig?') ? 'Nein, klären' :
                     message.content.includes('È corretto?') ? 'No, chiarire' :
                     message.content.includes('Está correto?') ? 'Não, esclarecer' :
                     'No, let me clarify'}
                  </Button>
                </div>
              </div>
            )}

            {/* Cart Items */}
            {message.cartItems && message.cartItems.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-600 flex items-center gap-1">
                    <ShoppingCart className="w-4 h-4" />
                    {t.readyToAdd} ({message.cartItems.length} items)
                  </p>
                  {message.cartItems.length > 1 && !message.cartItems.every(i => isItemAddedForMessage(index, i.rshd_id)) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAddAllToCart(message.cartItems, index)}
                      className="text-xs border-green-500 text-green-600 hover:bg-green-50"
                      data-testid="add-all-to-cart-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" /> {t.addAll}
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {message.cartItems.map((item, idx) => (
                    <div 
                      key={item.rshd_id || idx}
                      className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200"
                      data-testid={`cart-item-${item.rshd_id || idx}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{item.item_name}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {item.store_name}
                        </p>
                        <p className="text-xs text-green-600 font-medium">
                          {item.suggested_quantity} × ${item.price_per_unit?.toFixed(2)} = ${item.subtotal?.toFixed(2)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAddToCart(item, index)}
                        disabled={isItemAddedForMessage(index, item.rshd_id)}
                        className={isItemAddedForMessage(index, item.rshd_id)
                          ? "bg-green-100 text-green-600 hover:bg-green-100 text-xs"
                          : "bg-green-600 hover:bg-green-700 text-white text-xs"
                        }
                        data-testid={`add-to-cart-btn-${item.rshd_id || idx}`}
                      >
                        {isItemAddedForMessage(index, item.rshd_id) ? (
                          <><Check className="w-3 h-3 mr-1" /> {t.added}</>
                        ) : (
                          <><Plus className="w-3 h-3 mr-1" /> {t.addToCart.split(' ')[0]} {item.suggested_quantity}</>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Proactive Offer - Search Other DealShaq Grocer Partners */}
            {message.proactiveOffer && (
              <div className="mt-4 p-4 border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl shadow-sm" data-testid="proactive-offer-indicator">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-purple-900">
                      {uiLanguage === 'spanish' ? '¿Te gustaría que busque en otras tiendas asociadas de DealShaq?' :
                       uiLanguage === 'french' ? 'Voulez-vous que je cherche dans d\'autres magasins partenaires DealShaq?' :
                       uiLanguage === 'german' ? 'Möchten Sie, dass ich in anderen DealShaq-Partnergeschäften suche?' :
                       uiLanguage === 'italian' ? 'Vorresti che cercassi in altri negozi partner DealShaq?' :
                       uiLanguage === 'portuguese' ? 'Gostaria que eu procurasse em outras lojas parceiras DealShaq?' :
                       'Would you like me to search other nearby DealShaq grocer partners?'}
                    </p>
                    <p className="text-xs text-purple-700 mt-1">
                      <strong>{uiLanguage === 'spanish' ? 'Artículos faltantes:' : 
                               uiLanguage === 'french' ? 'Articles manquants:' :
                               uiLanguage === 'german' ? 'Fehlende Artikel:' :
                               uiLanguage === 'italian' ? 'Articoli mancanti:' :
                               uiLanguage === 'portuguese' ? 'Itens em falta:' :
                               'Missing items:'}</strong> {message.proactiveOffer.missing_items?.slice(0, 10).join(', ')}
                      {message.proactiveOffer.missing_items?.length > 10 && ` (${uiLanguage === 'spanish' ? 'y' : uiLanguage === 'french' ? 'et' : uiLanguage === 'german' ? 'und' : uiLanguage === 'italian' ? 'e' : uiLanguage === 'portuguese' ? 'e' : 'and'} ${message.proactiveOffer.missing_items.length - 10} ${uiLanguage === 'spanish' ? 'más' : uiLanguage === 'french' ? 'de plus' : uiLanguage === 'german' ? 'mehr' : uiLanguage === 'italian' ? 'di più' : uiLanguage === 'portuguese' ? 'mais' : 'more'})`}
                    </p>
                    <p className="text-xs text-purple-600 mt-1">
                      {uiLanguage === 'spanish' ? 'Puedo revisar' :
                       uiLanguage === 'french' ? 'Je peux vérifier' :
                       uiLanguage === 'german' ? 'Ich kann prüfen' :
                       uiLanguage === 'italian' ? 'Posso controllare' :
                       uiLanguage === 'portuguese' ? 'Posso verificar' :
                       'I can check'} {message.proactiveOffer.nearby_stores?.slice(0, 3).join(', ')}
                      {message.proactiveOffer.additional_stores_count > 0 && ` (${uiLanguage === 'spanish' ? 'y' : 'and'} ${message.proactiveOffer.additional_stores_count} ${uiLanguage === 'spanish' ? 'más' : 'more'})`}
                      {' '}{uiLanguage === 'spanish' ? 'dentro de' :
                            uiLanguage === 'french' ? 'dans un rayon de' :
                            uiLanguage === 'german' ? 'innerhalb von' :
                            uiLanguage === 'italian' ? 'entro' :
                            uiLanguage === 'portuguese' ? 'dentro de' :
                            'within'} {message.proactiveOffer.expanded_radius} {uiLanguage === 'spanish' ? 'millas de ti' :
                            uiLanguage === 'french' ? 'miles de vous' :
                            uiLanguage === 'german' ? 'Meilen von Ihnen' :
                            uiLanguage === 'italian' ? 'miglia da te' :
                            uiLanguage === 'portuguese' ? 'milhas de você' :
                            'miles of you'}.
                    </p>
                    <div className="flex gap-3 mt-3">
                      <Button
                        size="sm"
                        onClick={() => {
                          const searchMsg = uiLanguage === 'spanish' ? 'Sí, por favor busca en esas otras tiendas' :
                                           uiLanguage === 'french' ? 'Oui, veuillez chercher dans ces autres magasins' :
                                           uiLanguage === 'german' ? 'Ja, bitte suchen Sie in diesen anderen Geschäften' :
                                           uiLanguage === 'italian' ? 'Sì, cerca in quei altri negozi' :
                                           uiLanguage === 'portuguese' ? 'Sim, por favor procure nessas outras lojas' :
                                           'Yes, please search those other stores';
                          setInputValue(searchMsg);
                          setTimeout(() => document.querySelector('form')?.requestSubmit(), 100);
                        }}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 h-9 font-medium"
                        data-testid="accept-expanded-search-btn"
                      >
                        <MapPin className="w-4 h-4 mr-2" /> {t.searchOtherStores}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const declineMsg = uiLanguage === 'spanish' ? 'No gracias, continúa con lo que encontraste' :
                                            uiLanguage === 'french' ? 'Non merci, continuez avec ce que vous avez trouvé' :
                                            uiLanguage === 'german' ? 'Nein danke, fahren Sie mit dem fort, was Sie gefunden haben' :
                                            uiLanguage === 'italian' ? 'No grazie, procedi con quello che hai trovato' :
                                            uiLanguage === 'portuguese' ? 'Não obrigado, continue com o que você encontrou' :
                                            'No thanks, proceed with what you found';
                          setInputValue(declineMsg);
                          setTimeout(() => document.querySelector('form')?.requestSubmit(), 100);
                        }}
                        className="border-purple-300 text-purple-700 hover:bg-purple-100 h-9"
                        data-testid="decline-expanded-search-btn"
                      >
                        {t.noThanks}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* No Preferred Stores - Explore DRLPs Prompt */}
            {message.noPreferredStores && (
              <div className="mt-3 pt-3 border-t border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 -mx-4 px-4 pb-3 rounded-b-2xl" data-testid="no-stores-warning">
                <div className="flex items-start gap-3">
                  <Store className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">
                      To see the DealShaq retail partners in an area, provide a delivery location address:
                    </p>
                    <p className="text-xs text-blue-700 mt-1">
                      Enter your address to explore nearby stores and their available deals.
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        onClick={() => setShowExploreDrlps(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white h-8"
                        data-testid="explore-stores-btn"
                      >
                        <MapPin className="w-3 h-3 mr-1" />
                        Explore Stores
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {}}
                        className="border-blue-300 text-blue-700 hover:bg-blue-100 h-8"
                        data-testid="dismiss-stores-prompt-btn"
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Insights shared */}
            {message.insights && message.insights.length > 0 && !message.isWelcome && (
              <div className="mt-3 pt-2 border-t border-gray-100">
                <p className="text-xs text-purple-600 flex items-center gap-1 mb-1">
                  <Lightbulb className="w-3 h-3" />
                  Tips shared
                </p>
                <div className="flex flex-wrap gap-1">
                  {message.insights.slice(0, 3).map((insight, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                      {insight.length > 40 ? insight.substring(0, 40) + '...' : insight}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Full screen backdrop - must be OUTSIDE the Card */}
      {isFullScreen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40" 
          onClick={() => setIsFullScreen(false)}
        />
      )}
      
      <Card 
        className={`flex flex-col border-2 border-purple-200 shadow-lg relative transition-all duration-300 ${
          isFullScreen 
            ? 'fixed inset-4 z-50 h-auto max-h-[calc(100vh-2rem)]' 
            : 'h-[600px]'
        }`} 
        data-testid="dealshaq-agent"
      >
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-3 rounded-t-lg flex items-center justify-between relative z-50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold">DealShaq Shopping Assistant (DSA)</h3>
            <p className="text-xs text-purple-200">{t.headerSubtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {sessionId && (
            <>
              <Badge variant="outline" className="bg-white/10 text-white border-white/30 text-xs">
                <History className="w-3 h-3 mr-1" />
                {getRequestCount()} {getRequestCount() === 1 ? 'Request' : 'Requests'}
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={copyConversation}
                className="text-white hover:bg-white/20 h-7 px-2"
                title="Copy conversation"
                data-testid="copy-conversation-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </>
          )}
          {/* Full Screen Button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsFullScreen(!isFullScreen)}
            className="text-white hover:bg-white/20 h-7 px-2"
            title={isFullScreen ? "Exit full screen" : "Full screen"}
            data-testid="fullscreen-toggle-btn"
          >
            {isFullScreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          {/* Cart Button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowCart(!showCart)}
            className="text-white hover:bg-white/20 h-7 px-2 relative"
            title="View cart"
            data-testid="cart-toggle-btn"
          >
            <ShoppingCart className="w-4 h-4" />
            {cartItems.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {cartItems.length}
              </span>
            )}
          </Button>
          <Button 
            size="sm" 
            variant="ghost"
            onClick={startNewConversation}
            className="text-white hover:bg-white/20 h-7 px-2"
            title="Start new conversation"
            data-testid="new-conversation-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Cart Sidebar */}
      {showCart && (
        <div className="absolute right-0 top-12 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[500px] overflow-hidden" data-testid="cart-sidebar">
          <div className="bg-green-600 text-white px-4 py-2 flex items-center justify-between">
            <span className="font-semibold flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" />
              Your Cart ({cartItems.length})
            </span>
            <Button size="sm" variant="ghost" onClick={() => setShowCart(false)} className="text-white hover:bg-white/20 h-6 w-6 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="p-3 max-h-[380px] overflow-y-auto">
            {cartLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : cartItems.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Your cart is empty</p>
            ) : (
              <div className="space-y-2">
                {cartItems.map((item) => (
                  <div key={item.rshd_id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.item_name}</p>
                      <p className="text-xs text-gray-500">{item.store_name}</p>
                      <p className="text-xs text-green-600">{item.quantity} × ${item.price_per_unit?.toFixed(2)} = ${item.subtotal?.toFixed(2)}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveFromCart(item.rshd_id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {cartItems.length > 0 && (
            <div className="border-t border-gray-200 p-3 bg-gray-50">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">Total:</span>
                <span className="font-bold text-green-600">
                  ${cartItems.reduce((sum, item) => sum + (item.subtotal || 0), 0).toFixed(2)}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleClearCart}
                className="w-full text-red-600 border-red-300 hover:bg-red-50"
              >
                Clear Cart
              </Button>
            </div>
          )}
        </div>
      )} 

      {/* Last Exchange Banner - Show when user has a previous session */}
      {showLastExchange && lastExchange && (
        <div className="mx-4 mt-3 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg shadow-sm" data-testid="last-exchange-banner">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <History className="w-4 h-4 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-800 mb-1">{t.continueFromLastRequest}</p>
              <p className="text-sm text-amber-700 truncate" title={lastExchange.user_query}>
                "{lastExchange.user_query}"
              </p>
              {lastExchange.cart_items?.length > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  {lastExchange.cart_items.length} {t.itemsWereSuggested}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1 flex-shrink-0">
              <Button
                size="sm"
                onClick={continueFromLastExchange}
                className="bg-amber-500 hover:bg-amber-600 text-white text-xs h-7 px-3"
                data-testid="continue-last-exchange-btn"
              >
                {t.continue}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={dismissLastExchange}
                className="text-amber-600 hover:bg-amber-100 text-xs h-7 px-3"
                data-testid="dismiss-last-exchange-btn"
              >
                {t.dismiss}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4 bg-gray-50">
        {messages.map((message, index) => renderMessage(message, index))}
        
        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-start mb-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white">
                <Bot className="w-4 h-4" />
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">
                    {isSearchingExpanded ? t.searchingNearbyStores : t.thinking}
                  </span>
                </div>
                {isSearchingExpanded && expandedSearchInfo && (
                  <div className="mt-2 text-xs text-purple-600 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    <span>
                      Checking {expandedSearchInfo.nearby_stores_count} stores within {expandedSearchInfo.expanded_radius} mi
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t.askMeAnything}
              className="w-full px-4 py-2.5 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-400 transition-all pr-12"
              disabled={isLoading}
              data-testid="agent-input"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <Sparkles className="w-4 h-4 text-purple-300" />
            </div>
          </div>
          <Button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            className="bg-purple-600 hover:bg-purple-700 rounded-xl px-4"
            data-testid="agent-send"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>
        <p className="text-xs text-gray-400 mt-2 text-center">
          {t.iRememberConversation}
        </p>
      </div>
      
      {/* Explore DRLPs Modal */}
      <ExploreDrlpsModal
        isOpen={showExploreDrlps}
        onClose={() => setShowExploreDrlps(false)}
        onStoreSelect={async (store) => {
          try {
            // Add store to user's DACDRLP list
            await api.post(`/dac/retailers/${store.id}`);
            toast.success(`Added ${store.name} to your preferred stores!`);
          } catch (err) {
            console.error('Error adding store:', err);
            toast.error('Could not add store to your list');
          }
        }}
      />
    </Card>
    </>
  );
};

export default DealShaqAgent;
